import { MSTContext } from "../../stores/main";
import { Dropdown, Input, Spin } from "antd";
import { CheckOutlined, SearchOutlined } from "@ant-design/icons";
import { observer } from "mobx-react-lite";
import { useCallback, useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import confirmationModal from "../BlingsModal/CustomConfirm";
import { ReactComponent as ChevronUpDownIcon } from "../../icons-and-animations/chevron-up-down.svg";
import "./ProjectBreadcrumb.scss";

type IOption = {
  value: string;
  label: string;
  updatedAt: string;
  key?: string;
};

type ProjectOptionSource = {
  id: string;
  title: string;
  updatedAt: string;
  projectAccountId: string;
};

type ProjectOptionGroups = {
  recent: IOption[];
  all: IOption[];
};

type FilteredProjectOptionGroups = ProjectOptionGroups & {
  hasVisibleProjects: boolean;
};

function compareProjectsByName(a: IOption, b: IOption): number {
  const aName = a.label ? a.label : a.value;
  const bName = b.label ? b.label : b.value;
  return aName.localeCompare(bName, undefined, { sensitivity: "base" });
}

function compareProjectsByUpdatedAtDesc(a: IOption, b: IOption): number {
  return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
}

function filterProjectOptions(
  projects: IOption[],
  searchTerm: string
): IOption[] {
  if (!searchTerm) return projects;

  const term = searchTerm.toLowerCase();
  return projects.filter(
    (p) =>
      p.label.toLowerCase().includes(term) ||
      p.value.toLowerCase().includes(term)
  );
}

function buildProjectOptionGroups(
  projects: ProjectOptionSource[]
): ProjectOptionGroups {
  const userHasMultipleAccounts = projects.some(
    (p) => p.projectAccountId !== projects[0]?.projectAccountId
  );

  const options = projects.map((project) => {
    const label =
      project.title +
      (userHasMultipleAccounts ? ` | ${project.projectAccountId}` : "");

    return {
      label,
      value: project.id,
      updatedAt: project.updatedAt,
      key: project.id,
    };
  });

  const recentProjects = [...options]
    .sort(compareProjectsByUpdatedAtDesc)
    .slice(0, 5);
  const recentProjectKeys = new Set(
    recentProjects.map((project) => project.key)
  );
  const allProjects = options
    .filter((project) => !recentProjectKeys.has(project.key))
    .sort(compareProjectsByName);

  return {
    recent: recentProjects,
    all: allProjects,
  };
}

function filterProjectOptionGroups(
  projectGroups: ProjectOptionGroups,
  searchTerm: string
): FilteredProjectOptionGroups {
  const recent = filterProjectOptions(projectGroups.recent, searchTerm);
  const all = filterProjectOptions(projectGroups.all, searchTerm);

  return {
    recent,
    all,
    hasVisibleProjects: recent.length > 0 || all.length > 0,
  };
}

export const ProjectBreadcrumb = observer(() => {
  const {
    platformStore: {
      allProjects: allProjectsFromStore,
      selectedProjectId,
      isLoadingProjectList,
      hasLoadedProjectList,
      hasUnsavedChanges,
      selectedProjectBasicInfo,
      loadProjects,
      resetProjectStore,
    },
    dynamicDataStore: {
      resetPerVideoSchemaAndData,
      setCurrentLiveControlSchema,
      setCurrentLiveControlData,
      setInitialLiveControlData,
    },
    currentTab,
  } = useContext(MSTContext);

  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const handleChange = useCallback(
    async (projectId: string) => {
      const route = `/${projectId}/${currentTab}/`;
      let confirmed = true;
      if (hasUnsavedChanges) {
        confirmed = await confirmationModal.confirm(
          "You have pending changes. Leaving this page will discard these changes"
        );
      }
      if (confirmed) {
        resetProjectStore();
        resetPerVideoSchemaAndData();
        setCurrentLiveControlSchema({});
        setCurrentLiveControlData({});
        setInitialLiveControlData({});
        setIsOpen(false);
        setSearchTerm("");
        history.push(route);
      }
    },
    [
      hasUnsavedChanges,
      currentTab,
      history,
      setCurrentLiveControlSchema,
      setCurrentLiveControlData,
      setInitialLiveControlData,
      resetPerVideoSchemaAndData,
      resetProjectStore,
    ]
  );

  const projectOptionGroups = buildProjectOptionGroups(
    allProjectsFromStore.slice()
  );
  const {
    recent: filteredRecent,
    all: filteredAll,
    hasVisibleProjects,
  } = filterProjectOptionGroups(projectOptionGroups, searchTerm);

  const projectTitle = selectedProjectBasicInfo?.title || "Select project";
  const isProjectListLoading = isLoadingProjectList && !hasLoadedProjectList;

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        setSearchTerm("");
        return;
      }

      if (!hasLoadedProjectList && !isLoadingProjectList) {
        loadProjects().catch((error) =>
          console.error("Failed to load projects from dropdown", error)
        );
      }
    },
    [hasLoadedProjectList, isLoadingProjectList, loadProjects]
  );

  const ProjectDropdownMenu = () => (
    <div className="project-dropdown-menu">
      <Input
        className="search-input"
        placeholder="Find project..."
        prefix={<SearchOutlined />}
        suffix={isProjectListLoading ? <Spin size="small" /> : null}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        autoFocus
      />
      <div className="project-list">
        {filteredRecent.length > 0 && (
          <div className="project-group">
            <div className="group-label">Recent projects</div>
            {filteredRecent.map((project) => (
              <div
                key={project.key || project.value}
                className={`project-item ${
                  project.value === selectedProjectId ? "selected" : ""
                }`}
                onClick={() => handleChange(project.value)}
              >
                <span className="project-title">{project.label}</span>
                {project.value === selectedProjectId && (
                  <CheckOutlined className="check-icon" />
                )}
              </div>
            ))}
          </div>
        )}
        {(filteredAll.length > 0 ||
          (isProjectListLoading && hasVisibleProjects)) && (
          <div className="project-group">
            <div className="group-label project-group-label-with-status">
              <span>All projects</span>
              {isProjectListLoading && hasVisibleProjects && (
                <span className="project-group-loading">
                  <Spin size="small" />
                  <span>Loading more...</span>
                </span>
              )}
            </div>
            {filteredAll.map((project) => (
              <div
                key={project.key || project.value}
                className={`project-item ${
                  project.value === selectedProjectId ? "selected" : ""
                }`}
                onClick={() => handleChange(project.value)}
              >
                <span className="project-title">{project.label}</span>
                {project.value === selectedProjectId && (
                  <CheckOutlined className="check-icon" />
                )}
              </div>
            ))}
          </div>
        )}
        {isProjectListLoading && !hasVisibleProjects && (
          <div className="project-list-loading">
            <Spin size="small" />
            <span>Loading projects...</span>
          </div>
        )}
        {!isProjectListLoading && !hasVisibleProjects && (
          <div className="no-results">No projects found</div>
        )}
      </div>
    </div>
  );

  return (
    <div className="project-breadcrumb">
      <span className="separator">/</span>
      <span className="project-name">{projectTitle}</span>
      <Dropdown
        className="project-dropdown"
        popupRender={() => <ProjectDropdownMenu />}
        trigger={["click"]}
        placement="bottomLeft"
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        <button className="chevron-button" type="button">
          <ChevronUpDownIcon className="dropdown-chevron" />
        </button>
      </Dropdown>
    </div>
  );
});
